import { Button } from '@teach/ui'
import { AccessSelect } from '@/features/editor/components/publish/AccessSelect'
import type { AccessType } from '../../../types'
import type { ArticleAccess } from '../useArticleAccess'
import { ArticleReviewPanel } from '../ArticleReviewPanel'
import { useMessages } from '@/i18n'
import { messages } from './messages'

interface Props {
  accessType: AccessType
  articleAccess: ArticleAccess
  /** Причина гашения селекта (новая статья, несохранённая форма) или `null`. */
  hint: string | null
  /** Статья на проверке — селект погашен; причину объясняют плашка и панель проверки. */
  frozen: boolean
}

const LoadFailed = ({ onRetry }: { onRetry: () => void }) => {
  const m = useMessages(messages)
  return (
    <div className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
      <span>{m.statusLoadFailed}</span>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {m.retry}
      </Button>
    </div>
  )
}

/** Плашка проверки + селект доступа + причина, по которой он погашен. */
export const ArticleAccessControl = ({ accessType, articleAccess, hint, frozen }: Props) => {
  const { moderation, moderationError, reloadModeration, applyAccess, isBusy, retryLabel } =
    articleAccess
  const m = useMessages(messages)
  return (
    <>
      {/* Ширина — как у селекта доступа ниже (та же сетка в две колонки). Панели нет
          (проверки не было) — обёртка пустая и не должна давать отступ. */}
      {moderation ? (
        <div className="grid gap-4 empty:hidden md:grid-cols-2">
          <ArticleReviewPanel
            moderation={moderation}
            onWithdraw={() => void applyAccess('private')}
            disabled={isBusy}
          />
        </div>
      ) : null}
      <div className="space-y-2">
        <AccessSelect
          access={accessType}
          onAccessChange={(next) => void applyAccess(next)}
          disabled={hint !== null || frozen || isBusy || moderation === undefined}
          // Как у курса: отсчёт `429` запрещает ровно повышение, закрыть можно всегда.
          raiseBlocked={retryLabel !== null}
          // «Не знаем» ≠ «нельзя»: без ответа модерации подсказку о праве не показываем.
          catalogAllowed={moderation?.canPublishToCatalog ?? true}
          retryLabel={retryLabel}
          label={m.articleAccess}
          showTmaStub={false}
          noteBeside
        />
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
        {hint === null && moderationError ? <LoadFailed onRetry={reloadModeration} /> : null}
      </div>
    </>
  )
}
